import React, { useCallback, useEffect, useState } from 'react';
import './ManageDossier.scss';
import { DashboardLayout } from '../layouts/dashboard-layout/DashboardLayout';
import { Api } from '../api';
import { ApplicationState } from '../app/store';
import { useDispatch, useSelector } from 'react-redux';
import { DossierMember } from '../models/DossierMember';
import { DossierRelationsTable } from '../features/dossier-relations-table/DossierRelationsTable';
import { UserAccountRoleEnum, UserRelationRoleEnum } from '../api/types';
import { NotificationHelper } from '../helpers/NotificationHelper';
import { useHistory } from 'react-router-dom';
import { ERouterPath } from '../features/router/ERouterPath';
import { Icon } from '../features/icon/Icon';
import { ConfirmModal } from '../features/confirm-modal/ConfirmModal';
import { ISessionAppState } from '../app/session/reducer';
import { DossierV2 } from '../models/DossierV2';
import { sessionActions } from '../app/session/actions';

const ManageDossier = (): JSX.Element => {
  const [dossierRelations, setDossierRelations] = useState<DossierMember[]>([]);
  const [, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<DossierMember|undefined>(undefined);
  const { user, dossier } = useSelector((state: ApplicationState) => state.session);
  const dispatch = useDispatch();
  const sessionState = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const setActiveDossierRelations = useCallback(
    (dossierRelations: DossierMember[]) => {
      if (!sessionState.dossier || !sessionState.relation) {
        throw new Error('Expected dossier and relation to be set in the session state');
      }
      dispatch(sessionActions.setDossier({ dossier: new DossierV2(sessionState.dossier), relation: new DossierMember(sessionState.relation), dossierRelations }));
    },
    [dispatch, sessionState]
  );
  const history = useHistory();

  const isAdminCaregiver = user?.accountRole === UserAccountRoleEnum.adminCaregiver;
  const isCaregiver = user?.accountRole === UserAccountRoleEnum.caregiver || isAdminCaregiver;
  const isAdmin = user?.accountRole === UserAccountRoleEnum.administration || isAdminCaregiver;

  const isAllowedToEdit = !dossier?.archived && (isCaregiver || isAdmin || isAdminCaregiver);

  const updateDossierRelations = useCallback(({ alsoUpdateState }: { alsoUpdateState?: boolean } = {}) => {
    if (!dossier) {
      throw new Error('A dossier must be selected');
    }

    setLoading(true);
    Api.listDossierRelations(dossier.id)
      .then(relations => {
        setDossierRelations(relations);
        if (alsoUpdateState) {
          setActiveDossierRelations(relations);
        }
      })
      .catch(err => console.error('Failed to load dossier relations', err))
      .finally(() => setLoading(false));
  }, [dossier, setActiveDossierRelations]);

  useEffect(() => {
    updateDossierRelations();
  }, [updateDossierRelations]);

  const handleChangeRole = (relation: DossierMember, newRole: UserRelationRoleEnum) => {
    if (relation.role === newRole) {
      return;
    }

    Api.updateDossierRelationRole(relation.memberId, newRole)
      .then(() => {
        NotificationHelper.toastSuccess('Rol geüpdatet', { silent: true });
        updateDossierRelations({ alsoUpdateState: true });
      })
      .catch(() => NotificationHelper.toastError('Updaten rol mislukt'));
  };

  const closeDeleteModal = () => setDeleting(undefined);

  const deleteConfirm = () => {
    if (!deleting) {
      return;
    }
    Api.deleteDossierRelation(deleting.memberId)
      .then(() => {
        updateDossierRelations({ alsoUpdateState: true });
        setDeleting(undefined);
        NotificationHelper.toastSuccess('Persoon is verwijderd uit dossier', { silent: true });
      })
      .catch(() => NotificationHelper.toastError('Verwijderen persoon uit dossier is mislukt'));
  };

  const handleDeleteRelation = (dossierRelation: DossierMember) => setDeleting(dossierRelation);

  const handleEditRelation = (dossierRelation: DossierMember) => history.push(ERouterPath.updatePerson.replace(':personId', `${dossierRelation.user.id}`));

  return (
    <>
      <DashboardLayout className="manage-dossier" noTabs={true} backButton={ERouterPath.dossier}>
        <div className="manage-dossier__header">
          <h2 className="manage-dossier__title">
            {
              isAllowedToEdit ? <>Deelnemers beheren</> : <>Alle deelnemers</>
            }
          </h2>
          <div className="manage-dossier__options">
            {
              isAllowedToEdit && dossier &&
              <button className="manage-dossier__btn manage-dossier__btn--add-person" onClick={() => history.push(`${ERouterPath.newPerson}/?add-to-dossier=${dossier.id}`)}>
                <Icon icon="add"/>
                  Nieuw persoon toevoegen
              </button>
            }
          </div>
        </div>
        <div className="manage-dossier__items">
          <DossierRelationsTable relations={dossierRelations} onChangeRole={handleChangeRole} onDeleteRelation={handleDeleteRelation} onEditRelation={handleEditRelation} isAllowedToEdit={isAllowedToEdit}/>
        </div>
      </DashboardLayout>

      {
        !!deleting &&
        <ConfirmModal
          title="Persoon verwijderen van dit dossier"
          body={<>Weet je zeker dat je <strong>{deleting?.user.name}</strong> wilt verwijderen van het dossier?</>}
          confirmLabel="Verwijderen"
          confirmButtonStyle="danger"
          onCancel={closeDeleteModal}
          onConfirm={deleteConfirm}
        />
      }
    </>
  );
};

export default ManageDossier;
